import { Button, createFormField, useMinDelayState } from '@ibsys/kit';
import { CheckIcon, CodeIcon, TypeIcon } from 'lucide-react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isConditionBuilt,
  type PredicateInputSource,
  type PredicateValue,
  useConvertPredicate,
} from '@/entities/predicate';
import type { PredicateIssue } from '@/shared/api';
import { AiInput, AiTextarea, PredicateIssues } from '@/shared/ui';
import { cn } from '@/shared/lib';

/** Запас справа, чтобы длинный текст не уходил под кнопки в конце поля; пересчитать при изменении их состава. */
const INPUT_TRAILING_SPACE_CLASS = 'pr-20!';

type Props = {
  value?: PredicateValue;
  onChange?: (value: PredicateValue) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  classNames?: { input?: string };
  type?: 'textarea' | 'input';
  label?: string;
  name?: string;
  required?: boolean;
  error?: string;
};

const PredicateInputBase: FC<Props> = React.memo((props) => {
  const {
    className,
    value,
    onChange,
    placeholder,
    disabled,
    classNames,
    type = 'textarea',
    label,
    required,
    error,
  } = props;

  const { t } = useTranslation('common');
  const { mutateAsync: convert, isPending } = useConvertPredicate();

  const [isHuman, setIsHuman] = useState(true);
  // Локальное состояние для отображения текста (не сбрасывается при проблемах записи)
  const [displayDsl, setDisplayDsl] = useState(value?.dsl || '');
  const [displayDslHuman, setDisplayDslHuman] = useState(value?.dslHuman || '');
  // Проблемы последней проверки записи; в значении они живут, пока условие не построено
  const [issues, setIssues] = useState<PredicateIssue[] | null>(value?.issues?.length ? value.issues : null);

  // Используем ref для отслеживания предыдущего value, чтобы определить изменения извне
  const prevValueRef = useRef<PredicateValue | undefined>(value);
  // Значение, отданное самим полем: его возврат из формы — не смена значения извне
  const emittedValueRef = useRef<PredicateValue | null>(null);

  // Используем displayValue для отображения, но value для контроля
  const displayTerm = isHuman ? displayDslHuman : displayDsl;
  const isMagic = useMinDelayState(isPending, 600);

  // Синхронизируем текст поля со значением, когда значение сменилось извне.
  // Значение с проблемами приходит только от самого поля — введённый текст в нём
  // не подменяется, иначе непригодная запись пропала бы вместе с проблемами.
  useEffect(() => {
    const prevValue = prevValueRef.current;
    const currentValue = value;
    // Обновляем ref для следующего рендера
    prevValueRef.current = currentValue;

    const valueChangedExternally =
      prevValue?.dsl !== currentValue?.dsl || prevValue?.dslHuman !== currentValue?.dslHuman;

    if (!valueChangedExternally) {
      return;
    }

    const emittedValue = emittedValueRef.current;
    const isOwnChange =
      !!emittedValue && emittedValue.dsl === currentValue?.dsl && emittedValue.dslHuman === currentValue?.dslHuman;

    // Форма вернула то, что поле только что отдало: перечень проблем уже показан и
    // относится к этому же ответу — стереть его значит скрыть отказ модели, пришедший
    // вместе с готовым условием
    if (isOwnChange) {
      emittedValueRef.current = null;
      return;
    }

    if (currentValue?.issues?.length) {
      setIssues(currentValue.issues);
      return;
    }

    setDisplayDsl(currentValue?.dsl ?? '');
    setDisplayDslHuman(currentValue?.dslHuman ?? '');
    setIssues(null);
  }, [value]);

  const emitChange = (nextValue: PredicateValue) => {
    emittedValueRef.current = nextValue;
    onChange?.(nextValue);
  };

  const clear = () => {
    setDisplayDsl('');
    setDisplayDslHuman('');
    setIssues(null);
    emitChange({
      dsl: '',
      dslHuman: '',
    });
  };

  const sendRequest = async (text: string) => {
    const isChanged = (isHuman ? value?.dslHuman : value?.dsl) !== text;
    if (isPending || !isChanged) {
      return;
    }

    const source: PredicateInputSource = isHuman ? 'description' : 'record';

    const response = await convert({
      dsl: isHuman ? undefined : text,
      dslHuman: isHuman ? text : undefined,
      entityType: 'conversation',
    });

    const responseIssues = response?.issues ?? [];

    setIssues(responseIssues.length ? responseIssues : null);

    if (isConditionBuilt(response, source)) {
      setDisplayDsl(response.dsl ?? '');
      setDisplayDslHuman(response.dslHuman ?? '');
      emitChange({
        dsl: response.dsl ?? '',
        dslHuman: response.dslHuman ?? '',
      });
      return;
    }

    // Условия нет: проблемы уходят в значение — сущность с ним не сохраняется,
    // а введённый текст остаётся в поле.
    emitChange({
      dsl: '',
      dslHuman: '',
      issues: responseIssues,
    });
  };

  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    if (!isMagic) {
      const newValue = event.target.value;
      // Обновляем только displayValue (визуальное отображение)
      if (isHuman) {
        setDisplayDslHuman(newValue);
      } else {
        setDisplayDsl(newValue);
      }
      setIssues(null);
    }
  };

  const handleBlur: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> = () => {
    if (displayTerm) {
      sendRequest(displayTerm);
    } else {
      clear();
    }
  };

  const handleKeyUp: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    if (type === 'input' && event.code === 'Enter') {
      if (displayTerm) {
        sendRequest(displayTerm);
      } else {
        clear();
      }
    }
  };

  const toggleDsl = () => {
    const next = !isHuman;
    setIsHuman(next);
  };

  const renderStatusIcon = () => {
    // Проблемы перечислены под полем, значком отмечаем только успешный разбор
    if (issues === null || issues.length > 0) {
      return null;
    }

    return (
      <div className="flex size-5 items-center justify-center rounded bg-success">
        <CheckIcon className="size-3 text-success-foreground" />
      </div>
    );
  };

  const iconRight = (
    <div className={'flex gap-2 items-center'}>
      {renderStatusIcon()}
      <Button
        tabIndex={-1}
        size={'sm'}
        variant={'ghost'}
        disabled={isMagic}
        icon={isHuman ? <CodeIcon /> : <TypeIcon />}
        onClick={toggleDsl}
        aria-label={isHuman ? t('predicateInputSwitchToRecord') : t('predicateInputSwitchToDescription')}
      />
    </div>
  );

  const commonProps = {
    iconRight,
    placeholder: placeholder || t('predicateInputPlaceholder'),
    value: displayTerm,
    label,
    required,
    className,
    disabled: disabled,
    isProcessing: isMagic,
    onPressUp: handleKeyUp,
    onBlur: handleBlur,
    onChange: handleChange,
    name: props.name,
    error,
  } as const;

  const field =
    type === 'textarea' ? (
      <AiTextarea {...commonProps} classNames={{ ...classNames, iconRight: label ? 'top-10' : 'top-5' }} />
    ) : (
      <AiInput
        {...commonProps}
        classNames={{ ...classNames, input: cn(classNames?.input, INPUT_TRAILING_SPACE_CLASS) }}
      />
    );

  return (
    <div className="flex flex-col gap-2">
      {field}
      {!!issues?.length && <PredicateIssues issues={issues} isConditionMissing={!value?.dsl} />}
    </div>
  );
});

PredicateInputBase.displayName = 'PredicateInputBase';

export const PredicateInput = createFormField(PredicateInputBase);
